// Champion kinds (dragon / golem / lich) — pure helpers that read everything
// from the snapshot's `legendRows` (the sim is the source of truth): the
// ability list per kind, how each ability is aimed, why a button is disabled,
// the unit's status badges and the Lich's Soul. Framework-free so every rule
// is unit-testable. Wire types: packages/protocol/src/legend.ts.

import type {
  LegendAbilityRow,
  LegendKindRow,
  LegendRows,
  SceneNode,
  UnitStatusProp,
} from '@aod/protocol';

/** The unit's stance statuses (a Golem in a city / dug in), the only ones that block it. */
export type StanceKind = 'garrison' | 'fortify';

/** Statuses the UI knows by name; any other kind degrades to a humanised label. */
export const KNOWN_STATUS_KINDS: ReadonlySet<string> = new Set([
  'wither',
  'fortify',
  'garrison',
  'ascended',
]);

/** `abilities[]` of a kind; a Dragon row from an older snapshot falls back to the legacy Firestorm. */
export function abilitiesOf(rows: LegendRows, kind: string): ReadonlyArray<LegendAbilityRow> {
  const row = rows.kinds[kind];
  if (!row) return [];
  if (row.abilities) return row.abilities;
  if (kind !== 'dragon') return [];
  return [
    {
      key: 'firestorm',
      shape: 'area_damage',
      range: row.abilityRange,
      radius: rows.firestorm.radius,
      cooldown: rows.firestorm.cooldown,
      dice: rows.firestorm.dice,
      status: null,
    },
  ];
}

/** Active statuses on a unit (empty while the module is off or none apply). */
export function unitStatuses(unit: SceneNode): ReadonlyArray<UnitStatusProp> {
  const s = unit.props?.statuses;
  return Array.isArray(s) ? (s as unknown as UnitStatusProp[]) : [];
}

export function hasStatus(unit: SceneNode, kind: string): boolean {
  return unitStatuses(unit).some((s) => s.kind === kind);
}

/** The stance a unit is in (garrisoned in a city, or fortified), if any. */
export function stanceOf(unit: SceneNode): StanceKind | null {
  if (hasStatus(unit, 'garrison')) return 'garrison';
  if (hasStatus(unit, 'fortify')) return 'fortify';
  return null;
}

/** "wither" -> "Wither" (last-resort label for a status kind the UI has no string for). */
export function humanizeKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** The unit's Soul counter (Lich); 0 for kinds without one. */
export function unitSoul(unit: SceneNode): number {
  const legend = unit.props?.legend as { soul?: number } | undefined;
  return legend?.soul ?? 0;
}

/**
 * How an ability is aimed, from its `shape` (never from the kind):
 * - `target`  : arm targeting, then click a hex within `range` (area_damage);
 * - `stance`  : buttons for the stance targets (own hex / adjacent owned city);
 * - `instant` : cast at once, the target is ignored (ascend).
 * An unknown shape is treated as `target` (the sim rejects what it cannot do).
 */
export type AimMode = 'target' | 'stance' | 'instant';

export function aimMode(ability: LegendAbilityRow): AimMode {
  switch (ability.shape) {
    case 'stance':
      return 'stance';
    case 'ascend':
      return 'instant';
    default:
      return 'target';
  }
}

export type AbilityBlock = 'cooldown' | 'soul' | 'ascended' | 'inert';

export interface AbilityView {
  readonly slot: number;
  readonly ability: LegendAbilityRow;
  readonly mode: AimMode;
  /** Remaining cooldown ticks. */
  readonly cooldown: number;
  /** Cooldown ring share 0..100 of the ability's cooldown still to run. */
  readonly ringPct: number;
  /** Why the ability cannot be used right now (null = ready). */
  readonly block: AbilityBlock | null;
}

/**
 * The ability buttons of one champion unit. A garrisoned Golem is inert except
 * for the Bulwark that lifts it; Ascend needs a full Soul and works once.
 */
export function abilityViews(
  rows: LegendRows,
  kind: string,
  unit: SceneNode,
  cooldowns: ReadonlyArray<number>,
): ReadonlyArray<AbilityView> {
  const stance = stanceOf(unit);
  const soul = unitSoul(unit);
  return abilitiesOf(rows, kind).map((ability, slot) => {
    const mode = aimMode(ability);
    const cooldown = cooldowns[slot] ?? 0;
    const ringPct =
      cooldown <= 0
        ? 0
        : Math.min(100, Math.round((cooldown / Math.max(1, ability.cooldown)) * 100));
    let block: AbilityBlock | null = null;
    if (mode === 'instant') {
      if (hasStatus(unit, 'ascended')) block = 'ascended';
      else if (soul < (rows.soul?.max ?? 100)) block = 'soul';
    } else if (mode === 'target' && stance === 'garrison') {
      block = 'inert';
    }
    if (block === null && cooldown > 0 && !(mode === 'stance' && stance !== null))
      block = 'cooldown';
    return { slot, ability, mode, cooldown, ringPct, block };
  });
}

/** The Lich-style Soul meter applies when any of the kind's abilities is an `ascend`. */
export function hasSoul(rows: LegendRows, kind: string): boolean {
  return !!rows.soul && abilitiesOf(rows, kind).some((a) => aimMode(a) === 'instant');
}

/** Hex distance on axial coordinates (kept local so this module stays dependency-free). */
function dist(aq: number, ar: number, bq: number, br: number): number {
  const dq = aq - bq;
  const dr = ar - br;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr));
}

export interface StanceTarget {
  readonly cityId: string;
  readonly name: string;
  readonly q: number;
  readonly r: number;
}

/** Owned, non-neutral cities within `range` hexes (a Golem can garrison into one). */
export function adjacentOwnCities(
  nodes: ReadonlyArray<SceneNode>,
  unit: SceneNode,
  localPlayer: number,
  range: number,
): ReadonlyArray<StanceTarget> {
  const uq = unit.props?.q as number | undefined;
  const ur = unit.props?.r as number | undefined;
  if (uq === undefined || ur === undefined) return [];
  const out: StanceTarget[] = [];
  for (const n of nodes) {
    if (n.type !== 'city' || n.props?.ownerId !== localPlayer || n.props?.neutral === true)
      continue;
    const q = n.props?.q as number | undefined;
    const r = n.props?.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    const d = dist(uq, ur, q, r);
    if (d >= 1 && d <= Math.max(1, range)) {
      out.push({ cityId: n.id, name: (n.props?.name as string | undefined) ?? n.id, q, r });
    }
  }
  return out.sort((a, b) => a.cityId.localeCompare(b.cityId));
}

/** Rows of every kind, in the wire's key order. */
export function kindEntries(rows: LegendRows): ReadonlyArray<readonly [string, LegendKindRow]> {
  return Object.entries(rows.kinds);
}
